import { useLocale, useTranslations, type Locale } from "next-intl";
import { LangItem } from "./LangItem";
import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { flags, languages } from "@/constants/lang";
import Image from "next/image";

export const LangSwitcher = ({
  showLocale,
  setShowLocale,
  isModal,
}: {
  showLocale?: boolean;
  isModal?: boolean;
  setShowLocale?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [lang, setLang] = useState<string | null>(null);
  const t = useTranslations();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 1020
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1020);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const getLangCookie = () => {
      const match = document.cookie.match(/(^| )lang=([^;]+)/);
      return match ? match[2] : null;
    };

    setLang(getLangCookie());
  }, []);

  const locale = useLocale();

  if (!!lang) {
    return (
      <div>
        {locale === "en" && <LangItem label="English" locale="en" noSelector />}
        {locale === "en-GB" && (
          <LangItem label="English (UK)" locale="en-GB" noSelector />
        )}
        {locale === "zh-CN" && (
          <LangItem label="中文 (简体)" locale="zh-CN" noSelector />
        )}
        {locale === "zh-TW" && (
          <LangItem label="中文 (繁體)" locale="zh-TW" noSelector />
        )}
        {locale === "vi" && (
          <LangItem label="Tiếng Việt" locale="vi" noSelector />
        )}
        {locale === "es" && <LangItem label="Español" locale="es" noSelector />}
        {locale === "de" && <LangItem label="Deutsch" locale="de" noSelector />}
        {locale === "ko" && <LangItem label="한국어" locale="ko" noSelector />}
        {locale === "fr" && (
          <LangItem label="Français" locale="fr" noSelector />
        )}
        {locale === "pt-PT" && (
          <LangItem label="Português (Portugal)" locale="pt-PT" noSelector />
        )}
        {locale === "pt-BR" && (
          <LangItem label="Português (Brasil)" locale="pt-BR" noSelector />
        )}
        {locale === "pl" && <LangItem label="Polski" locale="pl" noSelector />}
      </div>
    );
  }

  const showModal = isMobile && showLocale;
  const activeLanguage = languages.find((l) => l.locale === locale)!;

  if (isModal) {
    if (!showModal) return <></>;
    return (
      <div className={`fixed inset-0 bg-white z-[51]`}>
        <div className="p-6">
          <X
            onClick={() => {
              if (setShowLocale) setShowLocale(false);
            }}
            size={18}
            className="text-black cursor-pointer absolute left-5"
          />

          <h5 className="font-inter text-sm font-semibold text-sleap-gray-950 text-center mb-0">
            {t("Other.language")}
          </h5>
        </div>

        {languages.map((lang: { label: string; locale: Locale }) => (
          <div
            key={lang.locale}
            className="border-t border-t-sleap-off-white-100"
          >
            <div className="px-6">
              <LangItem
                key={lang.locale}
                label={lang.label}
                locale={lang.locale}
              />{" "}
            </div>
          </div>
        ))}
      </div>
    );
  } else {
    return (
      <div>
        <div className="lg:hidden block px-2">
          <button
            type="button"
            className="flex gap-2 p-2 items-center justify-between w-full rounded-none font-switzer text-base font-semibold"
          >
            <span className="flex gap-2 items-center">
              <Image //@ts-ignore
                src={`/assets/v2/icons/flags/${flags[locale]}.svg`}
                height={20}
                alt="icon"
                width={20}
              />
              <span className="text-nowrap">{activeLanguage.label}</span>
            </span>
            <Check className="text-sleap-purple-300 block" />
          </button>
        </div>
        <div className="lg:block hidden">
          {languages.map((lang: { label: string; locale: Locale }) => (
            <LangItem
              key={lang.locale}
              label={lang.label}
              locale={lang.locale}
            />
          ))}
        </div>
      </div>
    );
  }
};
